
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { PACKAGES, COUNTRIES, CATEGORIES } from '../data';
import { PackageCard } from './Home';

// --- Shared Internal Components ---

const DropdownWrapper: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  children: React.ReactNode; 
  className?: string 
}> = ({ isOpen, onClose, children, className }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      ref={wrapperRef}
      className={`fixed inset-x-4 top-20 md:absolute md:inset-x-auto md:top-full md:left-0 mt-2 z-[200] bg-white rounded-lg shadow-[0_15px_50px_rgba(0,0,0,0.15)] border border-gray-100 animate-fade-in md:w-auto w-[calc(100vw-32px)] ${className}`}
    >
      {children}
    </div>
  );
};

const CalendarPicker: React.FC<{ onClose: () => void; onSelect: (val: string) => void }> = ({ onClose, onSelect }) => {
  const [viewDate, setViewDate] = useState(new Date(2026, 0, 1)); 
  const [startDate, setStartDate] = useState<Date | null>(new Date(2026, 0, 2)); 
  const [endDate, setEndDate] = useState<Date | null>(new Date(2026, 0, 16));    
  
  const days = ['PR', 'OT', 'TR', 'CE', 'PK', 'SE', 'SV'];
  const monthNames = [
    'Janvāris', 'Februāris', 'Marts', 'Aprīlis', 'Maijs', 'Jūnijs',
    'Jūlijs', 'Augusts', 'Septembris', 'Oktobris', 'Novembris', 'Decembris'
  ];

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; 
  };

  const generateMonthData = (year: number, month: number) => {
    const totalDays = getDaysInMonth(year, month);
    const startOffset = getFirstDayOfMonth(year, month);
    const prevMonthDays = getDaysInMonth(year, month - 1);
    const grid = [];

    for (let i = startOffset - 1; i >= 0; i--) {
      grid.push({ date: new Date(year, month - 1, prevMonthDays - i), current: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      grid.push({ date: new Date(year, month, i), current: true });
    }
    const remaining = 42 - grid.length;
    for (let i = 1; i <= remaining; i++) {
      grid.push({ date: new Date(year, month + 1, i), current: false });
    }
    return grid;
  };

  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(null);
      } else {
        setEndDate(date);
      }
    }
  };

  const isInRange = (date: Date) => {
    return startDate && endDate && date > startDate && date < endDate;
  };

  const changeMonth = (offset: number) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1);
    setViewDate(newDate);
  };

  const renderMonth = (date: Date, showPrevBtn = false, showNextBtn = false) => {
    const monthData = generateMonthData(date.getFullYear(), date.getMonth());
    return (
      <div className="flex-1 w-full min-w-[280px]">
        <div className="flex items-center justify-center mb-6 relative">
          {showPrevBtn && (
            <button 
              type="button" 
              onClick={() => changeMonth(-1)}
              className="absolute left-0 w-8 h-8 rounded-md border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-chevron-left text-[10px]"></i>
            </button>
          )}
          <h4 className="text-sm font-bold text-[#05073C]">
            {monthNames[date.getMonth()]} {date.getFullYear()}
          </h4>
          {showNextBtn && (
            <button 
              type="button" 
              onClick={() => changeMonth(1)}
              className="absolute right-0 w-8 h-8 rounded-md border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-chevron-right text-[10px]"></i>
            </button>
          )}
        </div>
        <div className="grid grid-cols-7 mb-2">
          {days.map(d => <div key={d} className="text-[9px] font-bold text-brand-deep/30 text-center py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {monthData.map((d, i) => {
            const start = startDate && d.date.getTime() === startDate.getTime();
            const end = endDate && d.date.getTime() === endDate.getTime();
            const range = isInRange(d.date);
            
            return (
              <div 
                key={i} 
                className={`relative text-[11px] font-bold py-2 text-center cursor-pointer transition-all rounded-md
                  ${!d.current ? 'text-brand-deep/10' : 'text-brand-deep hover:bg-orange-50 hover:text-brand-accent'}
                  ${start ? 'bg-brand-deep text-white z-10 !text-white' : ''}
                  ${end ? 'bg-brand-deep text-white z-10 !text-white' : ''}
                  ${range ? 'bg-orange-50 text-brand-accent' : ''}
                `}
                onClick={() => d.current && handleDateClick(d.date)}
              >
                {d.date.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const secondMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

  return (
    <div className="md:w-[680px] w-full max-h-[85vh] md:max-h-none overflow-y-auto custom-scrollbar overflow-x-hidden">
      <div className="p-4 md:p-8 flex flex-col md:flex-row gap-8 md:gap-12 relative">
        {renderMonth(viewDate, true, window.innerWidth < 768)}
        <div className="hidden md:block absolute left-1/2 top-10 bottom-10 w-[1px] bg-gray-100 -translate-x-1/2"></div>
        <div className="hidden md:block">
           {renderMonth(secondMonthDate, false, true)}
        </div>
      </div>
      <div className="p-4 flex flex-col md:flex-row justify-between items-center border-t border-gray-100 gap-4 bg-gray-50/50">
        <button 
          type="button"
          onClick={() => { setStartDate(null); setEndDate(null); onSelect(''); }}
          className="w-full md:w-auto px-6 py-2.5 text-xs font-bold text-brand-deep/60 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
        >
          Notīrīt
        </button>
        <button 
          type="button"
          onClick={() => {
            if (startDate) {
              const startStr = `${startDate.getDate()}. ${monthNames[startDate.getMonth()].slice(0, 3)}.`;
              const endStr = endDate ? ` - ${endDate.getDate()}. ${monthNames[endDate.getMonth()].slice(0, 3)}.` : '';
              onSelect(`${startStr}${endStr}`);
            }
            onClose();
          }}
          className="w-full md:w-auto px-10 py-3 bg-brand-accent hover:bg-brand-accentHover text-white font-bold text-xs uppercase tracking-widest rounded-md transition-all shadow-md"
        >
          APSTIPRINĀT
        </button>
      </div>
    </div>
  );
};

// --- Page Components ---

interface FilterAccordionProps {
  title: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const FilterAccordion: React.FC<FilterAccordionProps> = ({ title, icon, isOpen, onToggle, children }) => {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 px-1 group"
      >
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 flex items-center justify-center text-brand-deep/30 group-hover:text-brand-accent transition-colors">
            <i className={`fas ${icon} text-base`}></i>
          </div>
          <span className="font-bold text-brand-deep text-sm tracking-tight">{title}</span>
        </div>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-[10px] text-brand-deep/20 transition-transform duration-300`}></i>
      </button>
      {isOpen && (
        <div className="pb-6 animate-fade-in pl-8 pr-2">
          {children}
        </div>
      )}
    </div>
  );
};

export const CelojumiListing: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const resultsRef = useRef<HTMLDivElement>(null);
  const queryParams = new URLSearchParams(location.search);
  const [showFilters, setShowFilters] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['price', 'resort', 'category']));
  const [activeHeroDropdown, setActiveHeroDropdown] = useState<string | null>(null);

  // Filter States
  const [filters, setFilters] = useState({
    destination: queryParams.get('destination') || '',
    minPrice: Number(queryParams.get('minPrice')) || 0,
    maxPrice: Number(queryParams.get('maxPrice')) || 5000,
    category: queryParams.get('category') || '',
    mealPlan: queryParams.get('mealPlan') || '',
    stars: Number(queryParams.get('stars')) || 0,
    lastMinute: queryParams.get('lastMinute') === 'true',
    roomTypes: queryParams.get('roomTypes')?.split(',').filter(Boolean) || [] as string[],
    amenities: queryParams.get('amenities')?.split(',').filter(Boolean) || [] as string[],
    people: Number(queryParams.get('people')) || 2,
    nights: Number(queryParams.get('nights')) || 7,
    dates: queryParams.get('dates') || '',
    priceMode: 'person' as 'person' | 'total'
  });

  const toggleSection = (id: string) => {
    const next = new Set(openSections);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpenSections(next);
  };

  const toggleFilterArray = (key: 'roomTypes' | 'amenities', value: string) => {
    setFilters(prev => {
      const next = prev[key].includes(value)
        ? prev[key].filter(t => t !== value)
        : [...prev[key], value];
      return { ...prev, [key]: next };
    });
  };

  // Sync state with URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.destination) params.set('destination', filters.destination);
    if (filters.category) params.set('category', filters.category);
    if (filters.minPrice > 0) params.set('minPrice', filters.minPrice.toString());
    if (filters.maxPrice < 5000) params.set('maxPrice', filters.maxPrice.toString());
    if (filters.mealPlan) params.set('mealPlan', filters.mealPlan);
    if (filters.stars > 0) params.set('stars', filters.stars.toString());
    if (filters.lastMinute) params.set('lastMinute', 'true');
    if (filters.people > 0) params.set('people', filters.people.toString());
    if (filters.nights > 0) params.set('nights', filters.nights.toString());
    if (filters.dates) params.set('dates', filters.dates);
    if (filters.roomTypes.length > 0) params.set('roomTypes', filters.roomTypes.join(','));
    if (filters.amenities.length > 0) params.set('amenities', filters.amenities.join(','));
    
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  const filteredPackages = useMemo(() => {
    return PACKAGES.filter(p => {
      const matchDest = !filters.destination || p.country === filters.destination || p.city === filters.destination;
      const matchPrice = p.price >= filters.minPrice && p.price <= filters.maxPrice;
      const matchCat = !filters.category || p.categories.includes(filters.category);
      const matchMeal = !filters.mealPlan || p.mealPlan === filters.mealPlan;
      const matchStars = !filters.stars || p.hotel.stars >= filters.stars;
      const matchLM = !filters.lastMinute || p.lastMinute === true;
      const matchPeople = !filters.people || p.people >= filters.people;
      const matchNights = !filters.nights || p.nights === filters.nights;
      
      return matchDest && matchPrice && matchCat && matchMeal && matchStars && matchLM && matchPeople && matchNights;
    });
  }, [filters]);

  const activeFilterList = useMemo(() => {
    const list = [];
    if (filters.destination) list.push({ key: 'destination', label: filters.destination });
    if (filters.category) list.push({ key: 'category', label: filters.category });
    if (filters.stars > 0) list.push({ key: 'stars', label: `${filters.stars}*` });
    if (filters.lastMinute) list.push({ key: 'lastMinute', label: 'Pēdējais brīdis' });
    if (filters.mealPlan) list.push({ key: 'mealPlan', label: filters.mealPlan });
    
    filters.roomTypes.forEach(rt => list.push({ key: `roomType-${rt}`, label: rt, type: 'roomTypes', originalValue: rt }));
    filters.amenities.forEach(am => list.push({ key: `amenity-${am}`, label: am, type: 'amenities', originalValue: am }));
    
    return list;
  }, [filters]);

  const clearFilters = () => {
    setFilters({ 
      destination: '', 
      minPrice: 0, 
      maxPrice: 5000, 
      category: '', 
      mealPlan: '', 
      stars: 0, 
      lastMinute: false, 
      roomTypes: [], 
      amenities: [],
      people: 2, 
      nights: 7, 
      dates: '',
      priceMode: 'person'
    });
  };

  const removeFilter = (item: any) => {
    if (item.type === 'roomTypes' || item.type === 'amenities') {
      toggleFilterArray(item.type, item.originalValue);
      return;
    }
    setFilters(prev => ({ 
      ...prev, 
      [item.key]: item.key === 'stars' || item.key === 'minPrice' || item.key === 'maxPrice' || item.key === 'people' || item.key === 'nights' ? 0 : item.key === 'lastMinute' ? false : '' 
    }));
  };

  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const AMENITIES = ['Baseins', 'SPA', 'Restorāns', 'Bērnu klubs', 'WiFi', 'Privātā pludmale'];
  const MEAL_PLANS = ['Viss iekļauts', 'Brokastis un vakariņas', 'Tikai brokastis', 'Bez ēdināšanas'];
  const ROOM_TYPES = ['Standarta numurs', 'Ģimenes numurs', 'Suite', 'Sea View'];

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[500px] md:min-h-[550px] flex flex-col items-center justify-center pb-12 md:pb-16 z-20">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2000&auto=format&fit=crop" 
            className="w-full h-full object-cover brightness-[0.5] scale-105"
            alt="Sunny Beach"
          />
          <div className="absolute inset-0 bg-brand-deep/50 backdrop-blur-[2px]"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-[1400px] px-6 md:px-12 lg:px-20 pt-12 text-center">
          <div className="flex items-center justify-center gap-2 text-[9px] font-black uppercase tracking-[0.2em] text-white/50 mb-6">
             <Link to="/" className="hover:text-brand-accent transition-colors">Galvenā Lapa</Link>
             <i className="fas fa-chevron-right text-[7px]"></i>
             <span className="text-white/80">Meklēt</span>
          </div>

          <h1 className="text-3xl md:text-7xl font-black mb-8 md:mb-10 text-white tracking-tighter leading-none uppercase drop-shadow-lg">
            Ceļojumi uz <br className="md:hidden" /> <span className="text-brand-accent">{filters.destination || 'Galamērķi'}</span>
          </h1>

          {/* Category Toggle Bar - Responsive */}
          <div className="inline-flex flex-wrap items-center justify-center bg-white/95 backdrop-blur-md rounded-lg p-1.5 mb-8 md:mb-10 shadow-xl border border-white/20 gap-1 relative z-30 w-full max-w-lg md:max-w-none">
             {[
               {id: 'all', label: 'Visi', icon: 'fa-sun'},
               {id: 'lastMinute', label: 'Pēdējā', icon: 'fa-fire-alt'},
               {id: 'exotic', label: 'Eksotiskie', icon: 'fa-globe-americas'},
               {id: 'ski', label: 'Slēpošanas', icon: 'fa-snowflake'},
               {id: 'combined', label: 'Kombinētās', icon: 'fa-route'}
             ].map((cat) => {
               const isActive = 
                 (cat.id === 'all' && !filters.lastMinute && !filters.category) ||
                 (cat.id === 'lastMinute' && filters.lastMinute) ||
                 (cat.id === 'exotic' && filters.category === 'Eksotiskie ceļojumi') ||
                 (cat.id === 'ski' && filters.category === 'Slēpošanas ceļojumi') ||
                 (cat.id === 'combined' && filters.category === 'Kombinētās tūres');

               return (
                 <button 
                   key={cat.id}
                   onClick={() => {
                      if (cat.id === 'all') setFilters(prev => ({ ...prev, category: '', lastMinute: false }));
                      else if (cat.id === 'lastMinute') setFilters(prev => ({ ...prev, category: '', lastMinute: true }));
                      else if (cat.id === 'exotic') setFilters(prev => ({ ...prev, category: 'Eksotiskie ceļojumi', lastMinute: false }));
                      else if (cat.id === 'ski') setFilters(prev => ({ ...prev, category: 'Slēpošanas ceļojumi', lastMinute: false }));
                      else if (cat.id === 'combined') setFilters(prev => ({ ...prev, category: 'Kombinētās tūres', lastMinute: false }));
                   }}
                   className={`flex items-center gap-2 px-3 md:px-6 py-2 rounded-md text-[8px] md:text-[10px] font-black uppercase tracking-wider transition-all
                     ${isActive 
                       ? 'bg-brand-accent text-white border-2 border-brand-deep shadow-md' 
                       : 'text-brand-deep/60 hover:bg-brand-surface border-2 border-transparent'}
                   `}
                 >
                   <i className={`fas ${cat.icon} text-[10px]`}></i>
                   <span className="hidden sm:inline">{cat.label}</span>
                   <span className="sm:hidden">{cat.id === 'combined' ? 'Komb' : cat.label}</span>
                 </button>
               );
             })}
          </div>
          
          <div className="bg-white rounded-lg shadow-[0_30px_100px_rgba(0,0,0,0.3)] max-w-[1250px] mx-auto p-4 md:p-5 flex flex-col md:flex-row items-center gap-4 relative z-40">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 flex-grow items-end text-left w-full">
              <div className="relative group w-full">
                <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Galamērķis</label>
                <div 
                  onClick={() => setActiveHeroDropdown(activeHeroDropdown === 'destination' ? null : 'destination')}
                  className="flex items-center justify-between border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                >
                  <span className={`text-sm font-bold truncate ${filters.destination ? 'text-brand-deep' : 'text-brand-deep/30'}`}>
                    {filters.destination || 'Izvēlies valsti'}
                  </span>
                  <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 group-hover:text-brand-accent"></i>
                </div>
                <DropdownWrapper isOpen={activeHeroDropdown === 'destination'} onClose={() => setActiveHeroDropdown(null)} className="w-full">
                  <div className="p-2 space-y-1 max-h-[300px] overflow-y-auto no-scrollbar">
                    {COUNTRIES.map(country => (
                      <div 
                        key={country.id} 
                        onClick={() => { setFilters({...filters, destination: country.name}); setActiveHeroDropdown(null); }}
                        className={`px-4 py-3 text-xs md:text-sm font-bold rounded-md cursor-pointer transition-colors ${filters.destination === country.name ? 'bg-brand-accent text-white' : 'text-brand-deep hover:bg-brand-surface'}`}
                      >
                         {country.name}
                      </div>
                    ))}
                  </div>
                </DropdownWrapper>
              </div>

              <div className="relative group w-full">
                <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Datumi</label>
                <div 
                  onClick={() => setActiveHeroDropdown(activeHeroDropdown === 'dates' ? null : 'dates')}
                  className="flex items-center gap-3 border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                >
                  <i className="far fa-calendar text-brand-deep/20 text-xs"></i>
                  <span className={`text-sm font-bold truncate ${filters.dates ? 'text-brand-deep' : 'text-brand-deep/30'}`}>
                    {filters.dates || 'Izvēlies periodu'}
                  </span>
                  <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 ml-auto group-hover:text-brand-accent"></i>
                </div>
                <DropdownWrapper isOpen={activeHeroDropdown === 'dates'} onClose={() => setActiveHeroDropdown(null)}>
                  <CalendarPicker onClose={() => setActiveHeroDropdown(null)} onSelect={(val) => setFilters({...filters, dates: val})} />
                </DropdownWrapper>
              </div>

              <div className="relative group w-full">
                <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Ceļotāji</label>
                <div 
                  onClick={() => setActiveHeroDropdown(activeHeroDropdown === 'people' ? null : 'people')}
                  className="flex items-center gap-3 border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                >
                  <i className="fas fa-user-group text-brand-deep/20 text-xs"></i>
                  <span className="text-sm font-bold text-brand-deep">
                    {filters.people}
                  </span>
                  <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 ml-auto group-hover:text-brand-accent"></i>
                </div>
                <DropdownWrapper isOpen={activeHeroDropdown === 'people'} onClose={() => setActiveHeroDropdown(null)} className="w-full md:w-[140px]">
                   <div className="p-2 space-y-1">
                      {[1, 2, 3, 4, 5, 6].map(n => (
                        <div 
                          key={n} 
                          onClick={() => { setFilters({...filters, people: n}); setActiveHeroDropdown(null); }}
                          className={`px-4 py-3 text-xs font-bold rounded-md cursor-pointer transition-all ${filters.people === n ? 'bg-brand-accent text-white' : 'text-brand-deep hover:bg-brand-surface'}`}
                        >
                          {n} {n === 1 ? 'persona' : 'personas'}
                        </div>
                      ))}
                   </div>
                </DropdownWrapper>
              </div>

              <div className="relative group w-full">
                <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Naktis</label>
                <div 
                  onClick={() => setActiveHeroDropdown(activeHeroDropdown === 'nights' ? null : 'nights')}
                  className="flex items-center gap-3 border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                >
                  <i className="far fa-moon text-brand-deep/20 text-xs"></i>
                  <span className="text-sm font-bold text-brand-deep">
                    {filters.nights}...
                  </span>
                  <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 ml-auto group-hover:text-brand-accent"></i>
                </div>
                <DropdownWrapper isOpen={activeHeroDropdown === 'nights'} onClose={() => setActiveHeroDropdown(null)} className="w-full md:w-[140px]">
                   <div className="p-2 space-y-1">
                      {[3, 5, 7, 10, 14, 21].map(n => (
                        <div 
                          key={n} 
                          onClick={() => { setFilters({...filters, nights: n}); setActiveHeroDropdown(null); }}
                          className={`px-4 py-3 text-xs font-bold rounded-md cursor-pointer transition-all ${filters.nights === n ? 'bg-brand-accent text-white' : 'text-brand-deep hover:bg-brand-surface'}`}
                        >
                          {n} naktis
                        </div>
                      ))}
                   </div>
                </DropdownWrapper>
              </div>
            </div>

            <button 
              type="button" 
              onClick={scrollToResults}
              className="bg-brand-accent text-white h-[54px] md:w-[200px] w-full rounded-lg font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-brand-accentHover transition-all shadow-lg active:scale-95"
            >
              Meklēt <i className="fas fa-magnifying-glass text-sm"></i>
            </button>
          </div>
        </div>
      </section>

      {/* MOBILE FILTERS TOGGLE */}
      <div className="lg:hidden sticky top-20 z-[40] bg-white border-b border-gray-100 p-4 flex items-center justify-between shadow-sm">
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 bg-brand-deep text-white px-5 py-2.5 rounded-md font-black text-[10px] uppercase tracking-widest"
        >
          <i className="fas fa-sliders-h"></i>
          Filtrēt
        </button>
        <div className="text-[10px] font-black text-brand-deep/40 uppercase tracking-widest">
          {filteredPackages.length} rezultāti
        </div>
      </div>

      <div ref={resultsRef} className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-20 py-8 md:py-12 relative z-10">
        <div className="flex flex-col lg:flex-row gap-10">
          
          {/* Sidebar Filters - Responsive Toggle */}
          <aside className={`
            ${showFilters ? 'fixed inset-0 z-[100] bg-white p-6 overflow-y-auto pt-24' : 'hidden'}
            lg:block lg:static lg:w-[320px] lg:space-y-8 lg:h-fit lg:sticky lg:top-40 lg:z-30 lg:bg-transparent lg:p-0
          `}>
            {/* Mobile Close Button */}
            <button 
              onClick={() => setShowFilters(false)}
              className="lg:hidden absolute top-6 right-6 w-10 h-10 bg-brand-surface rounded-full flex items-center justify-center text-brand-deep"
            >
              <i className="fas fa-times"></i>
            </button>

            <div className="bg-white lg:border lg:border-gray-100 rounded-lg lg:p-6 md:p-8 space-y-8 lg:shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              
              {/* Tava izvēle Section */}
              <div className="bg-gray-50/80 p-5 md:p-6 rounded-md border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-brand-deep text-sm tracking-tight">Tava izvēle:</h3>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {activeFilterList.map(f => (
                    <div key={f.key} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-gray-100 shadow-sm group">
                      <span className="text-[10px] md:text-[11px] font-bold text-brand-deep/80">{f.label}</span>
                      <button onClick={() => removeFilter(f)} className="text-brand-accent/40 hover:text-brand-accent transition-colors">
                        <i className="fas fa-xmark text-[9px]"></i>
                      </button>
                    </div>
                  ))}
                  {activeFilterList.length === 0 && <span className="text-brand-deep/30 text-[10px] md:text-[11px] font-bold italic py-1">Nav aktīvu filtru</span>}
                </div>

                <button 
                  onClick={clearFilters}
                  className="text-blue-500 font-bold text-[10px] md:text-[11px] hover:text-blue-600 transition-all uppercase tracking-widest"
                >
                  Dzēst visu
                </button>
              </div>

              {/* Accordion Filters */}
              <div className="divide-y divide-gray-50 text-left">
                <FilterAccordion title="Viesnīca" icon="fa-building" isOpen={openSections.has('hotel')} onToggle={() => toggleSection('hotel')}>
                   <div className="relative">
                    <input type="text" placeholder="Meklēt viesnīcu..." className="w-full p-3 bg-gray-50 rounded-md border border-gray-100 text-[13px] font-bold text-brand-deep outline-none focus:ring-1 ring-brand-accent/50" />
                    <i className="fas fa-search absolute right-3 top-1/2 -translate-y-1/2 text-brand-deep/20 text-xs"></i>
                   </div>
                </FilterAccordion>

                <FilterAccordion title="Cenu amplitūda, €" icon="fa-wallet" isOpen={openSections.has('price')} onToggle={() => toggleSection('price')}>
                   <div className="space-y-6">
                     <div className="flex p-1 bg-gray-50 rounded-md border border-gray-100">
                        <button 
                          onClick={() => setFilters({...filters, priceMode: 'person'})}
                          className={`flex-1 py-2 text-[10px] md:text-[11px] font-bold rounded-md transition-all ${filters.priceMode === 'person' ? 'bg-white text-brand-deep shadow-sm' : 'text-brand-deep/40'}`}
                        >
                          Personai
                        </button>
                        <button 
                          onClick={() => setFilters({...filters, priceMode: 'total'})}
                          className={`flex-1 py-2 text-[10px] md:text-[11px] font-bold rounded-md transition-all ${filters.priceMode === 'total' ? 'bg-white text-brand-deep shadow-sm' : 'text-brand-deep/40'}`}
                        >
                          Viss kopā
                        </button>
                     </div>
                     <div className="px-1">
                       <div className="flex justify-between mb-3">
                          <span className="text-[10px] md:text-[11px] font-bold text-brand-deep/40">0€</span>
                          <span className="text-[10px] md:text-[11px] font-bold text-brand-deep">{filters.maxPrice}€</span>
                       </div>
                       <input 
                          type="range" 
                          min="0" 
                          max="5000" 
                          step="50"
                          value={filters.maxPrice}
                          onChange={(e) => setFilters({...filters, maxPrice: Number(e.target.value)})}
                          className="w-full h-1 bg-gray-200 appearance-none cursor-pointer accent-brand-deep rounded-full"
                        />
                     </div>
                   </div>
                </FilterAccordion>

                <FilterAccordion title="Kūrorts" icon="fa-location-dot" isOpen={openSections.has('resort')} onToggle={() => toggleSection('resort')}>
                   <div className="space-y-3 pt-1 max-h-[220px] overflow-y-auto custom-scrollbar pr-1">
                    {COUNTRIES.map(c => (
                      <label key={c.id} className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={filters.destination === c.name}
                          onChange={() => setFilters({...filters, destination: filters.destination === c.name ? '' : c.name})}
                          className="w-4 h-4 rounded border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all"
                        />
                        <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </FilterAccordion>

                <FilterAccordion title="Atlases kritēriji" icon="fa-list-check" isOpen={openSections.has('criteria')} onToggle={() => toggleSection('criteria')}>
                   <div className="space-y-3 pt-1">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input type="checkbox" checked={filters.lastMinute} onChange={() => setFilters({...filters, lastMinute: !filters.lastMinute})} className="w-4 h-4 rounded border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all" />
                        <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">Pēdējā brīža</span>
                      </label>
                   </div>
                </FilterAccordion>

                <FilterAccordion title="Viesnīcas kategorija" icon="fa-star" isOpen={openSections.has('category')} onToggle={() => toggleSection('category')}>
                   <div className="space-y-3 pt-1">
                    {[5, 4, 3].map(star => (
                      <label key={star} className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="stars"
                          checked={filters.stars === star}
                          onChange={() => setFilters({...filters, stars: star})}
                          className="w-4 h-4 rounded-full border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all"
                        />
                        <div className="flex items-center gap-0.5 text-[#FFD700] text-[10px]">
                           {Array.from({length: star}).map((_, i) => <i key={i} className="fas fa-star"></i>)}
                        </div>
                      </label>
                    ))}
                  </div>
                </FilterAccordion>

                <FilterAccordion title="Ēdināšanas tips" icon="fa-utensils" isOpen={openSections.has('meal')} onToggle={() => toggleSection('meal')}>
                  <div className="space-y-3 pt-1">
                    {MEAL_PLANS.map(plan => (
                      <label key={plan} className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="mealPlan"
                          checked={filters.mealPlan === plan}
                          onChange={() => setFilters({...filters, mealPlan: plan})}
                          className="w-4 h-4 rounded-full border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all"
                        />
                        <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">{plan}</span>
                      </label>
                    ))}
                  </div>
                </FilterAccordion>

                <FilterAccordion title="Viesnīcas atrašanās vieta" icon="fa-compass" isOpen={openSections.has('location')} onToggle={() => toggleSection('location')}>
                   <div className="space-y-3 pt-1">
                      {['Pirmā līnija pie jūras', 'Pilsētas centrs', 'Kalnu pievājē'].map(loc => (
                         <label key={loc} className="flex items-center gap-3 cursor-pointer group">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all" />
                            <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">{loc}</span>
                         </label>
                      ))}
                   </div>
                </FilterAccordion>

                <FilterAccordion title="Ērtības" icon="fa-concierge-bell" isOpen={openSections.has('amenities')} onToggle={() => toggleSection('amenities')}>
                   <div className="space-y-3 pt-1">
                      {AMENITIES.map(am => (
                         <label key={am} className="flex items-center gap-3 cursor-pointer group">
                            <input 
                              type="checkbox" 
                              checked={filters.amenities.includes(am)}
                              onChange={() => toggleFilterArray('amenities', am)}
                              className="w-4 h-4 rounded border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all" 
                            />
                            <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">{am}</span>
                         </label>
                      ))}
                   </div>
                </FilterAccordion>

                <FilterAccordion title="Aktīvā atpūta" icon="fa-person-hiking" isOpen={openSections.has('active')} onToggle={() => toggleSection('active')}>
                   <div className="space-y-3 pt-1">
                      {['Teniss', 'Ūdenssports', 'Velonoma'].map(act => (
                         <label key={act} className="flex items-center gap-3 cursor-pointer group">
                            <input type="checkbox" className="w-4 h-4 rounded border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all" />
                            <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">{act}</span>
                         </label>
                      ))}
                   </div>
                </FilterAccordion>

                <FilterAccordion title="Numura tips" icon="fa-bed" isOpen={openSections.has('room')} onToggle={() => toggleSection('room')}>
                   <div className="space-y-3 pt-1">
                      {ROOM_TYPES.map(rt => (
                         <label key={rt} className="flex items-center gap-3 cursor-pointer group">
                            <input 
                              type="checkbox" 
                              checked={filters.roomTypes.includes(rt)}
                              onChange={() => toggleFilterArray('roomTypes', rt)}
                              className="w-4 h-4 rounded border-gray-200 text-brand-accent focus:ring-brand-accent/20 transition-all" 
                            />
                            <span className="text-[13px] font-medium text-brand-deep/70 group-hover:text-brand-accent transition-colors">{rt}</span>
                         </label>
                      ))}
                   </div>
                </FilterAccordion>
              </div>

              {/* Mobile CTA Apply Button */}
              <button 
                onClick={() => setShowFilters(false)}
                className="lg:hidden w-full bg-brand-accent text-white py-4 rounded-lg font-black uppercase text-xs tracking-widest mt-8 shadow-lg shadow-orange-100"
              >
                Labi, rādīt rezultātus
              </button>
            </div>
          </aside>

          {/* Results Grid Content Area */}
          <div className="flex-1 space-y-6 md:space-y-8">
            <div className="bg-white lg:border lg:border-gray-100 rounded-lg p-3 md:px-8 md:py-4 flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6 lg:shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
               <div className="flex items-center gap-3">
                  <span className="text-brand-accent font-black text-lg">{filteredPackages.length}</span>
                  <span className="text-brand-deep/40 text-[9px] md:text-[10px] font-black uppercase tracking-widest">piedāvājumi atrasti</span>
               </div>
               
               <div className="flex items-center gap-4 md:gap-6">
                  <div className="hidden md:flex items-center gap-1.5 bg-gray-50 p-1 rounded-md border border-gray-100">
                     <button className="w-8 h-8 flex items-center justify-center text-brand-accent bg-white rounded-md shadow-sm"><i className="fas fa-table-cells text-xs"></i></button>
                     <button className="w-8 h-8 flex items-center justify-center text-brand-deep/20 hover:text-brand-deep transition-colors"><i className="fas fa-list text-xs"></i></button>
                  </div>

                  <div className="relative">
                    <select className="appearance-none bg-transparent pr-8 outline-none text-brand-deep font-bold text-[10px] md:text-[11px] uppercase tracking-widest cursor-pointer hover:text-brand-accent transition-colors">
                      <option value="popularity">Populārākie</option>
                      <option value="price_asc">Cena pieaugoši</option>
                      <option value="price_desc">Cena dilstoši</option>
                    </select>
                    <i className="fas fa-chevron-down absolute right-2 top-1/2 -translate-y-1/2 text-[8px] md:text-[9px] text-brand-deep/20 pointer-events-none"></i>
                  </div>
               </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
              {filteredPackages.length > 0 ? (
                filteredPackages.map(pkg => (
                  <PackageCard key={pkg.id} pkg={pkg} />
                ))
              ) : (
                <div className="col-span-full bg-white rounded-lg p-12 md:p-20 text-center border-2 border-dashed border-gray-100">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="fas fa-plane-slash text-xl md:text-2xl text-brand-deep/10"></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold mb-3 text-brand-deep tracking-tight">Netika atrasts neviens ceļojums</h3>
                  <p className="text-brand-deep/40 font-medium mb-8 md:mb-10 max-w-sm mx-auto text-xs md:text-sm">Mēģiniet mainīt meklēšanas kritērijus vai atiestatiet visus filtrus.</p>
                  <button 
                    onClick={clearFilters}
                    className="bg-brand-deep text-white px-8 md:px-10 py-3 md:py-4 rounded-md font-bold text-[10px] md:text-[11px] uppercase tracking-widest transition-all hover:bg-brand-accent"
                  >
                    Atiestatīt filtrus
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
